import { Flex, ProviderConsumer, Text } from '@fluentui/react-northstar';
import i18next from 'i18next';
import React from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getTruncatedFilename, startFileUploadSession } from '../../shared/components/EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { platformService } from '../../shared/services/platform.service';
import { uploadService } from '../../shared/services/upload.service';
import { toastDefault } from '../../shared/utils/constants';
import { CustomFormAttachmentsProps } from '../../templates/CustomForms/CustomForm.Interface';

export interface FileUploadProgress {
  name: string;
  percent: number;
}

interface UploadedFormFiles {
  TemplateFormId: number;
  Files: Awaited<ReturnType<typeof startFileUploadSession>>;
  Message: string;
  IsPrivate: boolean;
}

// The attach call skips files for a form it was not sent, so a form that was not answered would only leave its
// uploads orphaned in SharePoint.
export const answeredFormFiles = (formFiles: CustomFormAttachmentsProps[], answeredForms: { TemplateFormId: number }[]) =>
  formFiles.filter(form => (form.Files?.length ?? 0) > 0 && answeredForms.some(answered => answered.TemplateFormId === form.TemplateFormId));

export const startingUploadProgress = (formFiles: CustomFormAttachmentsProps[]): FileUploadProgress[] =>
  formFiles.flatMap(form => form.Files.map(file => ({ name: file.FileName, percent: 0 })));

/**
 * Uploads each answered form's files to a new ticket and attaches them. AddTicket has already saved the answers, so
 * a file is the most a closed page or a failed call can lose, and the requester is told which ones.
 */
export const uploadFormFiles = async (api: platformService, uploadHelper: uploadService, ticketId: number, formFiles: CustomFormAttachmentsProps[], onProgress: (progress: FileUploadProgress[]) => void) => {
  const progress = startingUploadProgress(formFiles);
  const warn = (e: BeforeUnloadEvent) => e.preventDefault();
  window.addEventListener('beforeunload', warn);

  try {
    const uploaded: UploadedFormFiles[] = [];
    let first = 0;
    for (const form of formFiles) {
      const offset = first;
      const files = await startFileUploadSession(api, uploadHelper, form.Files, ticketId, false, "", (index: number, percent: number) => {
        progress[offset + index] = { ...progress[offset + index], percent };
        onProgress([...progress]);
      });
      uploaded.push({ TemplateFormId: form.TemplateFormId, Files: files, Message: form.Message, IsPrivate: form.IsPrivate });
      first += form.Files.length;
    }

    // startFileUploadSession reports 100 only once a file has finished uploading.
    let notAttached = progress.filter(file => file.percent < 100);
    if (notAttached.length < progress.length) {
      try {
        // Without answers: this call applies every answer it is given again, and Add Comment would post a second
        // comment unless its value matched what AddTicket stored exactly. A comment field without the Internal
        // Note box leaves IsPublic out, and a line break, a quote or a {{token}} breaks the match too.
        await api.attachWebCustomFormFilesOrThrow(ticketId, uploaded.map(form => ({ TemplateFormId: form.TemplateFormId })), uploaded, true);
      } catch {
        notAttached = progress;
      }
    }

    if (notAttached.length > 0)
      toast.error(i18next.t('requests.dialog.files-not-attached', { files: notAttached.map(file => file.name).join(', ') }), { ...toastDefault, autoClose: false });
  } finally {
    window.removeEventListener('beforeunload', warn);
  }
};

interface PortalUploadProgressProps {
  files: FileUploadProgress[];
}

export const PortalUploadProgress = ({ files }: PortalUploadProgressProps) => {
  const { t } = useTranslation();

  return (
    <ProviderConsumer render={globalTheme => (
      <Flex column gap='gap.small' hAlign='center' className='m-4'>
        <Text content={t('requests.dialog.uploading')} />
        {files.map((file, index) => (
          <Flex key={index} gap='gap.small' vAlign='center'>
            <div style={{ width: '1.25rem', height: '1.25rem' }}>
              <CircularProgressbar value={file.percent} strokeWidth={12} styles={buildStyles({ pathColor: globalTheme.siteVariables.colorScheme.brand.foreground, trailColor: globalTheme.siteVariables.colorScheme.default.border })} />
            </div>
            <Text content={getTruncatedFilename(file.name)} />
          </Flex>
        ))}
      </Flex>
    )} />
  );
};
